import { Calendar as CalendarIcon, DollarCircle, Megaphone, WarningCircle } from 'iconoir-react'
import { startCase } from 'lodash'
import { ContactJson } from 'app/types/entities'
import { getFieldIdsBySection } from './contactFieldConfig'
import { isSMCOrg } from './isSMCOrg'
import { UserJson } from 'app/types/entities'

// Non-draggable section IDs - prevents typos and makes maintenance easier
export const NON_DRAGGABLE_SECTION_IDS = [`contact-info`, `tags`] as const

export type NonDraggableSectionId = (typeof NON_DRAGGABLE_SECTION_IDS)[number]

export interface SectionItem {
  id: string
  name: string
  visible: boolean
  icon?: React.ElementType
  isSpotlight?: boolean
  children?: SectionItem[]
  originalParentId?: string // Used to track original parent when moved to spotlight
  originalIndex?: number // Used to track original position when moved to spotlight
  isCore?: boolean // Marks core fields that must remain in spotlight
  isSMC?: boolean // Field/section hidden for SMC org
  isIntegrated?: boolean // Children require integration to be visible
  superUserOnly?: boolean // Field/section only visible to super users
}

export const DEFAULT_CONTACT_DRAWER_SECTIONS: SectionItem[] = [
  {
    id: `contact-info`,
    name: `Contact Info`,
    visible: true,
  },
  {
    id: `tags`,
    name: `Tags`,
    visible: true,
  },
  {
    id: `spotlight`,
    name: `Spotlight`,
    visible: true,
    children: [
      // Core spotlight fields (always in spotlight)
      ...getFieldIdsBySection(`spotlight`).map((fieldId) => ({
        id: fieldId,
        name: startCase(fieldId),
        visible: true,
        isCore: true,
      })),
      // Fields that start in spotlight but belong to other sections
      {
        id: `email`,
        name: `Email`,
        visible: true,
        isSpotlight: true,
        originalParentId: `information`,
        isCore: false,
      },
      {
        id: `cellPhone`,
        name: `Cell Phone`,
        visible: true,
        isSpotlight: true,
        originalParentId: `information`,
        isCore: false,
      },
      {
        id: `qualification`,
        name: `Qualification`,
        visible: true,
        isSpotlight: true,
        originalParentId: `marketing`,
        isCore: false,
      },
      {
        id: `source`,
        name: `Source`,
        visible: true,
        isSpotlight: true,
        originalParentId: `marketing`,
        isSMC: false,
        isCore: false,
      },
      {
        id: `medium`,
        name: `Medium`,
        visible: true,
        isSpotlight: true,
        originalParentId: `marketing`,
        isSMC: false,
        isCore: false,
      },
      {
        id: `campaign`,
        name: `Campaign`,
        visible: true,
        isSpotlight: true,
        originalParentId: `marketing`,
        isSMC: false,
        isCore: false,
      },
      {
        id: `channel`,
        name: `Channel`,
        visible: true,
        isSpotlight: true,
        originalParentId: `marketing`,
        isCore: false,
      },
    ],
  },
  {
    id: `pipelines`,
    name: `Pipelines`,
    visible: true,
  },
  {
    id: `additional-fields`,
    name: `Additional Fields`,
    visible: true,
    children: [
      {
        id: `information`,
        name: `Information`,
        visible: true,
        icon: WarningCircle,
        children: getFieldIdsBySection(`information`)
          .filter((fieldId) => ![`email`, `cellPhone`].includes(fieldId)) // Exclude fields that start in spotlight
          .map((fieldId) => ({
            id: fieldId,
            name: startCase(fieldId),
            visible: true,
            superUserOnly: fieldId === `neededAction`,
          })),
      },
      {
        id: `financial`,
        name: `Financial`,
        visible: true,
        icon: DollarCircle,
        children: getFieldIdsBySection(`financial`).map((fieldId) => ({
          id: fieldId,
          name: startCase(fieldId),
          visible: true,
        })),
      },
      {
        id: `dates`,
        name: `Dates`,
        visible: true,
        icon: CalendarIcon,
        children: getFieldIdsBySection(`dates`).map((fieldId) => ({
          id: fieldId,
          name: startCase(fieldId),
          visible: true,
        })),
      },
      {
        id: `marketing`,
        name: `Marketing`,
        visible: true,
        icon: Megaphone,
        children: getFieldIdsBySection(`marketing`)
          .filter((fieldId) => ![`qualification`, `channel`, `source`, `medium`, `campaign`].includes(fieldId)) // Exclude fields that start in spotlight
          .map((fieldId) => ({
            id: fieldId,
            name: startCase(fieldId),
            visible: true,
          })),
      },
    ],
  },
  {
    id: `stats`,
    name: `Stats`,
    visible: true,
    isIntegrated: true,
  },
]

/**
 * Merge missing fields from default configuration into custom configuration
 * This ensures new fields are automatically included in existing custom configurations
 */
function mergeWithDefaultFields(customConfig: SectionItem[]): SectionItem[] {
  const defaultConfig = DEFAULT_CONTACT_DRAWER_SECTIONS

  // Helper to check if a field exists anywhere in the custom config (including spotlight)
  const fieldExistsInConfig = (fieldId: string, config: SectionItem[]): boolean => {
    for (const section of config) {
      if (section.children) {
        // Check direct children (for spotlight fields)
        if (section.children.some((child) => child.id === fieldId)) {
          return true
        }
        // Check nested children (for fields within sections like marketing, information, etc.)
        for (const child of section.children) {
          if (child.children && child.children.some((grandchild) => grandchild.id === fieldId)) {
            return true
          }
        }
      }
    }
    return false
  }

  return customConfig.map((customSection) => {
    const defaultSection = defaultConfig.find((ds) => ds.id === customSection.id)
    if (!defaultSection || !defaultSection.children || !customSection.children) {
      return customSection
    }

    // For each child section in custom config, merge missing fields from default
    const mergedChildren = customSection.children.map((customChild) => {
      const defaultChild = defaultSection.children?.find((dc) => dc.id === customChild.id)
      if (!defaultChild || !defaultChild.children || !customChild.children) {
        return customChild
      }

      // Get all field IDs that exist in default but not in custom
      const customFieldIds = customChild.children.map((f) => f.id)
      // Only add fields that don't exist anywhere in the custom config (prevents duplicates when fields are moved to spotlight)
      const missingFields = defaultChild.children.filter(
        (df) => !customFieldIds.includes(df.id) && !fieldExistsInConfig(df.id, customConfig),
      )

      // Add missing fields to the end of the custom configuration
      // Preserve icon from default if custom doesn't have one
      return {
        ...customChild,
        icon: customChild.icon || defaultChild.icon,
        children: [...customChild.children, ...missingFields],
      }
    })

    return {
      ...customSection,
      icon: customSection.icon || defaultSection.icon,
      children: mergedChildren,
    }
  })
}

/**
 * Get default spotlight order based on organization type
 * @param orgId - Organization ID to check if SMC
 * @returns Array of field IDs in default order
 */
export function getDefaultSpotlightOrder(orgId?: string): string[] {
  const isSmcOrg = orgId && isSMCOrg(orgId)

  if (isSmcOrg) {
    // SMC Default order
    return [`lifecycle`, `leadStatus`, `leadOwner`, `channel`, `qualification`, `cellPhone`, `email`]
  } else {
    // Trackable Default order
    return [
      `lifecycle`,
      `leadStatus`,
      `leadOwner`,
      `email`,
      `cellPhone`,
      `qualification`,
      `source`,
      `medium`,
      `campaign`,
      `channel`,
    ]
  }
}

/**
 * Get default contact drawer sections with organization-specific spotlight ordering
 * @param orgId - Organization ID for organization-specific defaults
 * @returns Default sections with proper spotlight ordering
 */
export function getDefaultContactDrawerSections(orgId?: string): SectionItem[] {
  const defaultOrder = getDefaultSpotlightOrder(orgId)

  return DEFAULT_CONTACT_DRAWER_SECTIONS.map((section) => {
    if (section.id !== `spotlight` || !section.children) {
      return section
    }

    return {
      ...section,
      children: section.children
        .filter((child) => {
          // Filter out SMC-excluded fields for SMC orgs
          if (child.isSMC === false && orgId && isSMCOrg(orgId)) {
            return false
          }
          return true
        })
        .sort((a, b) => {
          const indexA = defaultOrder.indexOf(a.id)
          const indexB = defaultOrder.indexOf(b.id)

          // If field is not in default order, put it at the end
          if (indexA === -1 && indexB === -1) return 0
          if (indexA === -1) return 1
          if (indexB === -1) return -1

          return indexA - indexB
        }),
    }
  })
}

export function getContactDrawerSections(customConfig?: SectionItem[], orgId?: string): SectionItem[] {
  if (!customConfig) {
    return getDefaultContactDrawerSections(orgId)
  }
  // Merge missing fields from default configuration to ensure new fields appear
  return mergeWithDefaultFields(customConfig)
}

/**
 * Check if a section is draggable based on its ID
 * @param sectionId - The section ID to check
 * @returns true if the section is draggable, false otherwise
 */
export function isSectionDraggable(sectionId: string): boolean {
  return !NON_DRAGGABLE_SECTION_IDS.includes(sectionId as NonDraggableSectionId)
}

/**
 * Strip icon properties from sections before saving to database
 * Icons cannot be serialized to JSON, so we remove them and restore from defaults when loading
 * @param sections - Array of sections to strip icons from
 * @returns Sections array without icon properties
 */
export function stripIconsForSaving(sections: SectionItem[]): SectionItem[] {
  return sections.map((section) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { icon, ...sectionWithoutIcon } = section
    return {
      ...sectionWithoutIcon,
      children: section.children?.map((child) => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { icon: childIcon, ...childWithoutIcon } = child
        return {
          ...childWithoutIcon,
          children: child.children,
        }
      }),
    }
  })
}

/**
 * Deep clone sections array to avoid mutating original state
 * @param sections - Array of sections to clone
 * @returns Deep cloned sections array
 */
export function cloneSections(sections: SectionItem[]): SectionItem[] {
  return sections.map((section) => ({
    ...section,
    icon: section.icon,
    children: section.children
      ? section.children.map((child) => ({
          ...child,
          icon: child.icon,
          children: child.children ? [...child.children] : undefined,
        }))
      : undefined,
  }))
}

/**
 * Find a section item by ID in a nested structure
 * @param sections - Array of sections to search
 * @param id - ID of the item to find
 * @returns Object containing the found item, its parent, and index, or null if not found
 */
export function findSectionItem(
  sections: SectionItem[],
  id: string,
): { item: SectionItem; parent: SectionItem | null; index: number } | null {
  for (const section of sections) {
    if (section.id === id) {
      return { item: section, parent: null, index: sections.indexOf(section) }
    }

    if (section.children) {
      for (const child of section.children) {
        if (child.id === id) {
          return { item: child, parent: section, index: section.children.indexOf(child) }
        }

        if (child.children) {
          for (const grandchild of child.children) {
            if (grandchild.id === id) {
              return { item: grandchild, parent: child, index: child.children.indexOf(grandchild) }
            }
          }
        }
      }
    }
  }

  return null
}

/**
 * Calculate if a section has visible children based on integration and lifecycle conditions
 * @param section - Section to check
 * @param isIntegrated - Whether integration is enabled
 * @param contact - Contact object for lifecycle stage check
 * @returns true if section has visible children
 */
export function hasVisibleChildren(section: SectionItem, isIntegrated?: boolean, contact?: any): boolean {
  return !!(
    section.children &&
    section.children.length > 0 &&
    !(section.isIntegrated && !isIntegrated && contact?.lifecycleStage !== `Patient`)
  )
}

/**
 * Filter sections based on integration, SMC org, and super user conditions
 * @param sections - Sections to filter
 * @param isIntegrated - Whether integration is enabled
 * @param contact - Contact object for org and lifecycle checks
 * @param user - User object for super user checks
 * @returns Filtered sections array
 */
export function filterSections(
  sections: SectionItem[],
  isIntegrated?: boolean,
  contact?: ContactJson,
  user?: UserJson,
): SectionItem[] {
  return sections
    .filter((section) => {
      if (section.isIntegrated && !isIntegrated) return false
      if (section.id === `pipelines` && !user?.workspaceId) return false
      return true
    })
    .map((section) => {
      if (!section.children) return section

      const filteredChildren = section.children
        .filter((child) => {
          if (child.isSMC === false && contact?.orgId && isSMCOrg(contact.orgId)) return false
          if (child.superUserOnly && user?.type !== `super`) return false
          return true
        })
        .map((child) => {
          if (!child.children) return child

          if (child.isIntegrated && !isIntegrated && contact?.lifecycleStage !== `Patient`) {
            return {
              ...child,
              children: [],
            }
          }

          const filteredGrandchildren = child.children.filter((grandchild) => {
            if (grandchild.isSMC === false && contact?.orgId && isSMCOrg(contact.orgId)) return false
            if (grandchild.superUserOnly && user?.type !== `super`) return false
            return true
          })

          return {
            ...child,
            children: filteredGrandchildren,
          }
        })

      return {
        ...section,
        children: filteredChildren,
      }
    })
}
